import { db, recoverFromIndexedDBQuota, type MaskGroupThumbnailRecord } from "./indexeddb"
import { isBrowserStorageQuotaError, isStorageQuotaError } from "@/utils/error"
import { Sentry } from "@/telemetry/sentry"

export const MASK_GROUP_THUMBNAIL_MAX_BYTES = 10 * 1024 * 1024
export const MASK_GROUP_THUMBNAIL_LOW_WATER_BYTES = 8 * 1024 * 1024

let reportedStorageFailure = false

function reportStorageFailure(error: unknown) {
	if (reportedStorageFailure) {
		return
	}
	reportedStorageFailure = true
	Sentry.captureException(error)
}

function getRecordId(projectId: string, photoId: string, groupId: string) {
	return `${projectId}:${photoId}:${groupId}`
}

async function getCacheSize() {
	const records = await db.maskGroupThumbnails.toArray()
	return records.reduce((total, record) => total + record.byteSize, 0)
}

async function evictTo(targetBytes: number) {
	const records = await db.maskGroupThumbnails.orderBy("updatedAt").toArray()
	let currentBytes = records.reduce((total, record) => total + record.byteSize, 0)
	const ids: string[] = []
	for (const record of records) {
		if (currentBytes <= targetBytes) {
			break
		}
		ids.push(record.id)
		currentBytes -= record.byteSize
	}
	await db.maskGroupThumbnails.bulkDelete(ids)
}

async function evictIfNeeded() {
	if ((await getCacheSize()) > MASK_GROUP_THUMBNAIL_MAX_BYTES) {
		await evictTo(MASK_GROUP_THUMBNAIL_LOW_WATER_BYTES)
	}
}

export async function readMaskGroupThumbnails(projectId: string, photoId: string) {
	try {
		return await db.maskGroupThumbnails
			.where("photoKey")
			.equals(`${projectId}:${photoId}`)
			.toArray()
	} catch (error) {
		reportStorageFailure(error)
		return []
	}
}

export async function touchMaskGroupThumbnails(ids: string[]) {
	if (ids.length === 0) {
		return
	}
	try {
		await db.maskGroupThumbnails.where("id").anyOf(ids).modify({ updatedAt: Date.now() })
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function writeMaskGroupThumbnail(input: {
	projectId: string
	photoId: string
	groupId: string
	transformKey: string
	maskGroupDigest: string
	data: Blob
}) {
	const record: MaskGroupThumbnailRecord = {
		id: getRecordId(input.projectId, input.photoId, input.groupId),
		photoKey: `${input.projectId}:${input.photoId}`,
		projectId: input.projectId,
		photoId: input.photoId,
		groupId: input.groupId,
		transformKey: input.transformKey,
		maskGroupDigest: input.maskGroupDigest,
		data: input.data,
		byteSize: input.data.size,
		updatedAt: Date.now(),
	}

	try {
		await db.maskGroupThumbnails.put(record)
		await evictIfNeeded()
		return true
	} catch (error) {
		if (isBrowserStorageQuotaError(error) || isStorageQuotaError(error) === true) {
			try {
				await recoverFromIndexedDBQuota(error)
				await db.maskGroupThumbnails.put(record)
				await evictIfNeeded()
				return true
			} catch (retryError) {
				reportStorageFailure(retryError)
				return false
			}
		}
		reportStorageFailure(error)
		return false
	}
}

export async function deleteMaskGroupThumbnail(
	projectId: string,
	photoId: string,
	groupId: string,
) {
	try {
		await db.maskGroupThumbnails.delete(getRecordId(projectId, photoId, groupId))
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function deleteMaskGroupThumbnailsForPhoto(projectId: string, photoId: string) {
	try {
		await db.maskGroupThumbnails.where("photoKey").equals(`${projectId}:${photoId}`).delete()
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function deleteMaskGroupThumbnailsForProject(projectId: string) {
	try {
		await db.maskGroupThumbnails.where("projectId").equals(projectId).delete()
	} catch (error) {
		reportStorageFailure(error)
	}
}

export async function clearMaskGroupThumbnails() {
	await db.maskGroupThumbnails.clear()
}

export function resetMaskGroupThumbnailStorageRuntimeState() {
	reportedStorageFailure = false
}
